import { Injectable, signal } from '@angular/core';

/**
 * Call-audio playback. Mirrors the prototype: lines are spoken live in Hindi
 * via the browser speech engine; production swaps speak() for Sarvam TTS.
 */
@Injectable({ providedIn: 'root' })
export class AudioService {
  readonly muted = signal<boolean>(false);
  private hiVoice: SpeechSynthesisVoice | null = null;

  constructor() {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      this.pickVoice();
      window.speechSynthesis.onvoiceschanged = () => this.pickVoice();
    }
  }

  private pickVoice() {
    const v = window.speechSynthesis.getVoices();
    const hi = v.filter((x) => /^hi([-_]|$)/i.test(x.lang) || /hindi|हिन/i.test(x.name));
    const female = ['swara', 'neerja', 'kalpana', 'ananya', 'google'];
    for (const n of female) {
      const m = hi.find((x) => x.name.toLowerCase().includes(n));
      if (m) { this.hiVoice = m; return; }
    }
    this.hiVoice = hi[0] || null;
  }

  toggleMute() {
    this.muted.update((m) => !m);
    if (this.muted() && typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
  }

  cancel() {
    if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
  }

  /** Speak a Hindi line; calls onDone when finished (or immediately if muted). */
  speak(text: string, who: 'agent' | 'customer', onDone: () => void) {
    if (this.muted() || typeof window === 'undefined' || !window.speechSynthesis) { onDone(); return; }
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'hi-IN';
      const isAgent = who === 'agent';
      if (this.hiVoice) u.voice = this.hiVoice;
      u.pitch = isAgent ? 1.0 : 1.12;
      u.rate = isAgent ? 0.96 : 0.99;
      let done = false;
      const finish = () => { if (!done) { done = true; onDone(); } };
      u.onend = finish;
      u.onerror = finish;
      window.speechSynthesis.speak(u);
      setTimeout(finish, Math.max(2200, text.length * 90) + 1500);
    } catch {
      onDone();
    }
  }
}
